// Табличные отчёты экрана «Стационар» (письмо Карасайской ЦРБ, ноябрь 2026):
//   • пролеченные случаи: отделение × источник финансирования (ГОБМП / ОСМС / прочие)
//   • лист услуг круглосуточного стационара по отделениям
//
// Бэкенд: dashboards/backend/app/routers/hospital_reports.py (/api/v1/hospital/report/*).
// Таблица/CSV-кирпичи — общие с отчётами АПП (AppReportSlides.tsx).
import React, { useState } from "react";
import {
  C, Status, Toolbar, btn, downloadCsv, fmtInt, scrollBox, tableStyle, td, tdNum, th, useReport,
} from "./AppReportSlides";
import type { Container, Fetcher, ReportFilters } from "./AppReportSlides";

export type HospitalReportFilters = ReportFilters & { place?: string | null };

type CasesHref = (p: { department_id?: number | null; finance_source_id?: number | null; place?: string | null }) => string;

// ────────────────────────────────────────────────────────────────────────────
// Отделение × источник финансирования
// ────────────────────────────────────────────────────────────────────────────
type DeptFinanceResp = {
  finance_columns: { name: string; finance_source_id: number | null }[];
  items: {
    department_id: number | null;
    department_name: string;
    place: string;
    by_finance: Record<string, number>;
    cases: number;
    bed_days: number;
  }[];
  total: { by_finance: Record<string, number>; cases: number; bed_days: number };
};

export function HospitalDeptFinanceSlide({ filters, fetcher, Container, casesHref }: {
  filters: HospitalReportFilters;
  fetcher: Fetcher;
  Container: Container;
  casesHref: CasesHref;
}) {
  const { data, loading, error } = useReport<DeptFinanceResp>(
    fetcher, "/api/v1/hospital/report/dept-finance", filters, { place: filters.place ?? undefined },
  );
  const cols = data?.finance_columns || [];
  const rows = data?.items || [];
  const open = (department_id: number | null, finance_source_id: number | null, place: string) =>
    window.open(casesHref({ department_id, finance_source_id, place }), "_blank", "noopener,noreferrer");
  const link: React.CSSProperties = { cursor: "pointer", textDecoration: "underline dotted" };

  const exportCsv = () =>
    downloadCsv("stacionar_otdeleniya_finansirovanie",
      ["Отделение", "КС/ДС", ...cols.map((c) => c.name), "Всего", "Койко-дни"],
      rows.map((r) => [r.department_name, r.place, ...cols.map((c) => r.by_finance[c.name] ?? 0), r.cases, r.bed_days]));

  return (
    <Container title="Пролеченные случаи по отделениям и источникам финансирования" hintRight="Клик по числу — список случаев">
      <Toolbar note="Количество случаев и койко-дни. Сумм по стационару МИС не передаёт.">
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!loading && !error && !!data && !rows.length} />
      {rows.length ? (
        <div style={scrollBox}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Отделение</th>
                <th style={th}>КС/ДС</th>
                {cols.map((c) => <th key={c.name} style={{ ...th, textAlign: "right" }}>{c.name}</th>)}
                <th style={{ ...th, textAlign: "right" }}>Всего</th>
                <th style={{ ...th, textAlign: "right" }}>Койко-дни</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.department_id}-${r.place}`}>
                  <td style={td}>{r.department_name}</td>
                  <td style={td}>{r.place}</td>
                  {cols.map((c) => {
                    const v = r.by_finance[c.name] ?? 0;
                    // «Прочие» — сборная колонка, одного id у неё нет: детализация только по ГОБМП/ОСМС.
                    const clickable = v > 0 && c.finance_source_id != null;
                    return (
                      <td key={c.name} style={clickable ? { ...tdNum, ...link } : tdNum}
                        onClick={clickable ? () => open(r.department_id, c.finance_source_id, r.place) : undefined}>
                        {v ? fmtInt(v) : ""}
                      </td>
                    );
                  })}
                  <td style={{ ...tdNum, fontWeight: 700, ...link }} onClick={() => open(r.department_id, null, r.place)}>{fmtInt(r.cases)}</td>
                  <td style={tdNum}>{fmtInt(r.bed_days)}</td>
                </tr>
              ))}
              {data ? (
                <tr>
                  <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Итого</td>
                  {cols.map((c) => <td key={c.name} style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(data.total.by_finance[c.name] ?? 0)}</td>)}
                  <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(data.total.cases)}</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(data.total.bed_days)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </Container>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Лист услуг КС по отделениям
// ────────────────────────────────────────────────────────────────────────────
type DeptServicesResp = {
  coverage: { cards_checked: number; cards_with_services: number; cases_total: number };
  items: {
    department: string;
    quantity: number;
    service_kinds: number;
    services: { code: string; name: string; cards: number; quantity: number }[];
  }[];
  total_quantity: number;
};

export function HospitalDeptServicesSlide({ filters, fetcher, Container }: {
  filters: HospitalReportFilters;
  fetcher: Fetcher;
  Container: Container;
}) {
  // Лист услуг просили именно по круглосуточному стационару; ДС в проверку почти не приходит.
  const place = filters.place || "КС";
  const { data, loading, error } = useReport<DeptServicesResp>(
    fetcher, "/api/v1/hospital/report/dept-services", filters, { place },
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [allOpen, setAllOpen] = useState(false);
  const rows = data?.items || [];
  const cov = data?.coverage;

  const exportCsv = () =>
    downloadCsv("stacionar_list_uslug", ["Отделение", "Код услуги", "Услуга", "Карт", "Количество"],
      rows.flatMap((d) => d.services.map((s) => [d.department, s.code, s.name, s.cards, s.quantity])));

  const note = cov
    ? `Услуги МИС присылает только в картах, отправленных на проверку: ${fmtInt(cov.cards_with_services)} карт с услугами `
      + `из ${fmtInt(cov.cases_total)} пролеченных случаев ${place} за период.`
    : undefined;

  return (
    <Container title={`Лист услуг ${place} по отделениям`} hintRight="Клик по отделению — раскрыть услуги">
      <Toolbar note={note}>
        <button style={btn} onClick={() => setAllOpen((v) => !v)}>{allOpen ? "Свернуть все" : "Раскрыть все"}</button>
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!loading && !error && !!data && !rows.length} />
      {rows.length ? (
        <div style={scrollBox}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Отделение / услуга</th>
                <th style={th}>Код</th>
                <th style={{ ...th, textAlign: "right" }}>Карт</th>
                <th style={{ ...th, textAlign: "right" }}>Количество</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const isOpen = allOpen || !!open[d.department];
                return (
                  <React.Fragment key={d.department}>
                    <tr style={{ cursor: "pointer" }} onClick={() => setOpen((o) => ({ ...o, [d.department]: !o[d.department] }))}>
                      <td style={{ ...td, fontWeight: 700 }}>{isOpen ? "▾" : "▸"} {d.department}</td>
                      <td style={{ ...td, color: C.muted }}>{fmtInt(d.service_kinds)} видов</td>
                      <td style={tdNum} />
                      <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(d.quantity)}</td>
                    </tr>
                    {isOpen
                      ? d.services.map((s) => (
                          <tr key={`${d.department}-${s.code}`}>
                            <td style={{ ...td, paddingLeft: 24 }}>{s.name || "—"}</td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>{s.code}</td>
                            <td style={tdNum}>{fmtInt(s.cards)}</td>
                            <td style={tdNum}>{fmtInt(s.quantity)}</td>
                          </tr>
                        ))
                      : null}
                  </React.Fragment>
                );
              })}
              <tr>
                <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Итого</td>
                <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(data?.total_quantity || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </Container>
  );
}
